import { useCallback, useState } from 'react';

/**
 * Local row-selection state for a multi-select table — CLM-69.
 *
 * The companion of `components/SelectionToolbar.tsx`, for a surface whose
 * selection is plain component state rather than a field of some other record
 * or a persisted store. Intended consumers: the future multi-select surfaces
 * (Verification queue "verify several", Notifications "acknowledge several",
 * the Extraction-pipeline re-run set of CLM-70) once their batch endpoints
 * exist. Neither of today's two selections uses it, deliberately:
 *
 * - Bulk ingest keeps its ticks in `surfaces/ingest/planner-store.ts` so they
 *   survive a NavRail unmount (D-67-6). That store resets the selection to the
 *   signed rows on every `setPlan` — it does **not** preserve the human's ticks
 *   across a re-plan. This hook is stricter (see below); it does not replace
 *   the store.
 * - The bundle upload keeps `include` inside `edits[row_id]` — one field of a
 *   per-row edit record (D-69-5). Splitting it out would mean two stores for
 *   one row.
 *
 * Re-plan rule (the hook's own): when `rows` changes identity, keys that no
 * longer exist are **pruned**, rows not previously seen are ticked iff
 * `defaultSelected(row)`, and every surviving row keeps whatever the human
 * decided. `reset()` returns to the default set over the current rows.
 *
 * Pass a **referentially stable** `rows` (component state, a store selector or
 * `useMemo`) — the reconcile keys off identity and runs a state update during
 * render, so a new array each render (`data.filter(...)` in the component
 * body) re-reconciles each render and trips React's re-render guard.
 *
 * `selectAll()` / `clear()` with no argument act on every row; with `keys`
 * they act on that subset only (a group's rows).
 */
export interface Selection {
  readonly selected: ReadonlySet<string>;
  readonly count: number;
  readonly total: number;
  readonly isSelected: (key: string) => boolean;
  readonly set: (keys: readonly string[], on: boolean) => void;
  readonly selectAll: (keys?: readonly string[]) => void;
  readonly clear: (keys?: readonly string[]) => void;
  readonly reset: () => void;
}

export interface UseSelectionOptions<T> {
  /** Ticked on first sight of the row; never re-applied to a row the human has already seen. */
  readonly defaultSelected?: (row: T) => boolean;
}

interface SelectionState<T> {
  /** The `rows` identity this state was reconciled against. */
  readonly rows: readonly T[];
  /** Every key ever shown — so the default applies to a row exactly once. */
  readonly seen: ReadonlySet<string>;
  readonly selected: ReadonlySet<string>;
}

function reconcile<T>(
  previous: SelectionState<T> | undefined,
  rows: readonly T[],
  keyOf: (row: T) => string,
  defaultSelected: ((row: T) => boolean) | undefined,
): SelectionState<T> {
  const present = new Set<string>();
  const seen = new Set<string>(previous?.seen ?? []);
  const selected = new Set<string>();
  for (const row of rows) {
    const key = keyOf(row);
    present.add(key);
    if (previous === undefined || !previous.seen.has(key)) {
      if (defaultSelected?.(row) === true) selected.add(key);
    } else if (previous.selected.has(key)) {
      selected.add(key);
    }
    seen.add(key);
  }
  return { rows, seen, selected };
}

export function useSelection<T>(
  rows: readonly T[],
  keyOf: (row: T) => string,
  options?: UseSelectionOptions<T>,
): Selection {
  const defaultSelected = options?.defaultSelected;
  const [state, setState] = useState<SelectionState<T>>(() =>
    reconcile(undefined, rows, keyOf, defaultSelected),
  );

  // Rows changed identity (a re-plan, a re-fetch): reconcile during render so
  // this very render already reports the pruned / defaulted set. React permits
  // a same-component state update during render and re-renders at once.
  let current = state;
  if (state.rows !== rows) {
    current = reconcile(state, rows, keyOf, defaultSelected);
    setState(current);
  }

  const set = useCallback((keys: readonly string[], on: boolean) => {
    setState((prev) => {
      const next = new Set(prev.selected);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return { ...prev, selected: next };
    });
  }, []);

  const selectAll = useCallback(
    (keys?: readonly string[]) => {
      setState((prev) => {
        const next = new Set(prev.selected);
        for (const key of keys ?? prev.rows.map(keyOf)) next.add(key);
        return { ...prev, selected: next };
      });
    },
    [keyOf],
  );

  const clear = useCallback(
    (keys?: readonly string[]) => {
      setState((prev) => {
        if (keys === undefined) return { ...prev, selected: new Set<string>() };
        const next = new Set(prev.selected);
        for (const key of keys) next.delete(key);
        return { ...prev, selected: next };
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selected: new Set(
        prev.rows.filter((row) => defaultSelected?.(row) === true).map(keyOf),
      ),
    }));
  }, [keyOf, defaultSelected]);

  const { selected } = current;
  return {
    selected,
    count: selected.size,
    total: rows.length,
    isSelected: (key) => selected.has(key),
    set,
    selectAll,
    clear,
    reset,
  };
}
